import React, {
  useContext,
  useEffect,
  useState,
  useImperativeHandle,
  memo,
} from "react";
import { Grid2, useMediaQuery, useTheme } from "@mui/material";

import ActionBar from "./ActionBar";
import defaultmobileComponent from "./defaultMobileComponentBehaviour";
import GridDefinitionsContext from "./GridDefinitionsContext";
import { gql, useQuery } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import createQuery from "./queryGeneration/createQuery";
import GridRowLayout from "./GridRowLayout";
import MobileGrid from "./MobileGrid";
import FabContext from "../Layout/Contexts/FabContext";

const DataGrid = (props) => {
  const context = useContext(GridDefinitionsContext);
  if (context === null) {
    return (
      <div>
        DevError: Griddefinitions and Apollo client should be implemented.
      </div>
    );
  }
  return (
    <GridLayoutComponent context={context} {...props}></GridLayoutComponent>
  );
};

const GridLayoutComponent = memo(
  ({
    context,
    onClick,
    isCards,
    toolbarOptions,
    getSelectedItemOptions,
    multiSelectedItemOptions,
    pageSize,
    type,
    getItemOptions,
    defaultFilters,
    defaultColumns,
    gridRef,
    hiddenColumns,
    mobileComponent,
    onSelectedItemChanged,
    selectedItems,
    forceMobile,
    mandatoryColumnNames,
  }) => {
    const [internalSelectedItems, setInternalSelectedItems] = useState([]);
    useImperativeHandle(gridRef, () => ({
      refetch() {
        refetch();
      },
      query: GET_DATA,
    }));

    const currentGridDefinition = context
      ? context.find((x) => x.typeName === type)
      : null;

    if (!currentGridDefinition) {
      console.log(`No datagrid definition found for ${type}`);
    }
    const columns = currentGridDefinition
      ? [...currentGridDefinition.columnDefinitions]
      : null;
    const currentMobileComponent =
      mobileComponent ?? defaultmobileComponent(columns);
    const { setFabItems } = useContext(FabContext);
    if (hiddenColumns) {
      hiddenColumns.forEach((x) => {
        const columnIndex = columns.findIndex((y) => y.propertyName === x);
        if (columnIndex !== -1) {
          columns.splice(columnIndex, 1);
        }
      });
    }
    const checkSelectedItems = [...(selectedItems || internalSelectedItems)];
    const fabItems = {
      toolbarOptions,
      getSelectedItemOptions: getSelectedItemOptions,
      multiSelectedItemOptions,
    };
    const [filters, setFilters] = useState([]);
    useEffect(() => {
      if (checkSelectedItems[1]) {
        setFabItems({
          key: "grid-" + type,
          items: fabItems.multiSelectedItemOptions,
        });
        return;
      } else if (checkSelectedItems[0]) {
        setFabItems({
          key: "grid-" + type,
          items: getSelectedItemOptions?fabItems.getSelectedItemOptions(checkSelectedItems[0]):[],
        });
        return;
      } else {
        setFabItems({ key: "grid-" + type, items: fabItems.toolbarOptions });
        return;
      }
    }, [selectedItems, internalSelectedItems, setFabItems]);

    let activeSortLocal = localStorage.getItem(`activeSort-${type}`);
    if (activeSortLocal == "undefined") {
      activeSortLocal = '"displayName"';
    }

    const [activeSort, setActiveSort] = useState(
      JSON.parse(activeSortLocal) ?? "displayName"
    );
    localStorage.setItem(`activeSort-${type}`, JSON.stringify(activeSort));

    const [sortInverted, setSortInverted] = useState(
      JSON.parse(localStorage.getItem(`sortInverted-${type}`))
    );
    localStorage.setItem(`sortInverted-${type}`, JSON.stringify(sortInverted));
    const [gridView, setGridView] = useState(
      JSON.parse(localStorage.getItem(`gridView`)) ?? true
    );
    localStorage.setItem(`gridView`, JSON.stringify(gridView));
    let localActivecolumns = JSON.parse(
      localStorage.getItem(`activeColumns-${type}`)
    );
    const [activeColumns, setActiveColumns] = useState(
      localActivecolumns ?? defaultColumns ?? ["displayName"]
    );
    localStorage.setItem(
      `activeColumns-${type}`,
      JSON.stringify(activeColumns)
    );
    const [pageIndex, setPageIndex] = useState(0);

    const [searchValue, setSearchValue] = useState("");
    const theme = useTheme();
    const navigate = useNavigate();
    const isXs = useMediaQuery(theme.breakpoints.down("sm"));
    const isMobile =
      !gridView || theme.isXs === true || (isXs && theme.isXs === undefined);
    const pageLength = pageSize ?? 10;
    const extraMandatory =
      mandatoryColumnNames != undefined ? "," + mandatoryColumnNames : "";
    const mandatoryColumns = ["id, uuid,displayName " + extraMandatory];

    const query = createQuery({
      type: currentGridDefinition.typeName,
      pageLength,
      columns,
      pageIndex,
      activeSort,
      sortInverted,
      defaultFilters,
      activeColumns:
        isMobile || forceMobile
          ? currentMobileComponent.mobileColumns
          : activeColumns,
      searchValue,
      filters,
      mandatoryColumns,
    });

    const GET_DATA = gql(query);
    const { error, data, refetch } = useQuery(GET_DATA);
    if (error) {
      if (
        error.message.includes(
          "The current user is not authorized to access this resource."
        )
      )
        navigate("/login");
      console.log({ error });
      return "an error has occured, check the console for more details.";
    }
    const dataObjects = data && Object.values(data)[0];
    const pageInfo = dataObjects?.pageInfo;

    const nextPage = () => {
      setPageIndex(pageIndex + 1);
    };
    const previousPage = () => {
      setPageIndex(pageIndex - 1);
    };

    const handleSetSelectedItems = (item) => {
      let newSelectedItems = [...(selectedItems || internalSelectedItems)];
      if (!multiSelectedItemOptions) {
        if (newSelectedItems[0] == item) {
          newSelectedItems = [];
        } else {
          newSelectedItems = [item];
        }

        onSelectedItemChanged
          ? onSelectedItemChanged(newSelectedItems)
          : setInternalSelectedItems(newSelectedItems);
      } else {
        const index = newSelectedItems.findIndex((x) => x.id === item.id);
        if (index !== -1) {
          newSelectedItems.splice(index, 1);
        } else {
          newSelectedItems.push(item);
        }
        onSelectedItemChanged
          ? onSelectedItemChanged(newSelectedItems)
          : setInternalSelectedItems(newSelectedItems);
      }
    };

    const handleSetFilters = (newFilters) => {
      setFilters(newFilters);
      setPageIndex(0);
    };

    const handleSetSearchValue = (newSearchValue) => {
      setSearchValue(newSearchValue);
      setPageIndex(0);
    };

    const toggleGridView = () => {
      setGridView(!gridView);
    };
    if (!currentGridDefinition || !columns) {
      return "No valid griddefinition selected";
    }

    return (
      <Grid2 container direction="column" spacing={isCards ? 1 : 0}>
        <Grid2 xs={12}>
          <ActionBar
            isCards={isCards}
            columns={columns}
            toolbarOptions={toolbarOptions}
            selectedItems={selectedItems || internalSelectedItems}
            setSelectedItems={onSelectedItemChanged || setInternalSelectedItems}
            filters={filters}
            setFilters={handleSetFilters}
            multiSelectedItemOptions={multiSelectedItemOptions}
            getSelectedItemOptions={getSelectedItemOptions}
            searchValue={searchValue}
            setSearchValue={handleSetSearchValue}
            toggleGridView={toggleGridView}
            gridView={gridView}
            typeName={type}
          />
        </Grid2>

        {!(isMobile || -forceMobile) ? (
          <GridRowLayout
            isCards={isCards}
            columns={columns}
            activeColumns={activeColumns}
            activeSort={activeSort}
            setActiveSort={setActiveSort}
            setActiveColumns={setActiveColumns}
            sortInverted={sortInverted}
            setSortInverted={setSortInverted}
            data={dataObjects}
            onClick={onClick}
            filters={filters}
            setPageIndex={setPageIndex}
            pageLength={pageLength}
            pageIndex={pageIndex}
            nextPage={nextPage}
            pageInfo={pageInfo}
            previousPage={previousPage}
            getSelectedItemOptions={getSelectedItemOptions}
            multiSelectedItemOptions={multiSelectedItemOptions}
            handleSetSelectedItems={handleSetSelectedItems}
            selectedItems={selectedItems || internalSelectedItems}
            getItemOptions={getItemOptions}
            type={type}
          />
        ) : (
          <Grid2 spacing={2} container>
            <MobileGrid
              onClick={onClick}
              handleSetSelectedItems={handleSetSelectedItems}
              data={dataObjects}
              getSelectedItemOptions={getSelectedItemOptions}
              multiSelectedItemOptions={multiSelectedItemOptions}
              columns={columns}
              MobileComponent={currentMobileComponent.getComponent()}
              selectedItems={selectedItems || internalSelectedItems}
            />
          </Grid2>
        )}
      </Grid2>
    );
  }
);

export default memo(DataGrid);
